/**
 * Development Tools Component
 *
 * Provides utilities for:
 * - Seeding test data directly in Firestore
 * - Viewing/debugging current state
 * - Quick tenant switching
 *
 * Only renders in development mode.
 */

import { useState } from 'react';
import { collection, doc, setDoc, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, Database, RefreshCw, Trash2, Bug } from 'lucide-react';
import { getDefaultPricingConfig } from '@/types/firestore';

// Test tenant ID - matches the script
const TEST_TENANT_ID = 'tenant_test_debug_001';

interface DevToolsProps {
  currentTenantId?: string;
  onTenantChange?: (tenantId: string) => void;
}

export function DevTools({ currentTenantId, onTenantChange }: DevToolsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSeeding, setIsSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState<{ success: boolean; message: string } | null>(null);

  // Only show in development
  if (import.meta.env.PROD) {
    return null;
  }

  const seedTestData = async () => {
    setIsSeeding(true);
    setSeedResult(null);

    try {
      // 1. Create Test Tenant
      const tenantRef = doc(db, 'tenants', TEST_TENANT_ID);
      await setDoc(tenantRef, {
        company_name: 'Debug Renovations Inc.',
        slug: 'debug-renovations',
        pricing_config: getDefaultPricingConfig(),
        branding_config: {
          logo_url: 'https://placehold.co/200x80/1e40af/white?text=Debug+Reno',
          primary_color: '#1e40af',
        },
        widget_config: {
          enabled: true,
          cta_book_call_url: 'https://calendly.com/test',
          cta_get_quote_url: 'https://example.com/quote',
          business_phone: '+1 (555) 123-4567',
          watermark_text: 'Debug Renovations',
        },
        subscription_status: 'active',
        subscription_tier: 'pro',
        generations_this_month: 42,
        created_at: Timestamp.now(),
      });

      // 2. Create Test User
      await setDoc(doc(db, `tenants/${TEST_TENANT_ID}/users`, 'test_user_001'), {
        email: 'debug@renovations-ai.test',
        role: 'owner',
        created_at: Timestamp.now(),
      });

      // 3. Create Sample Leads
      const sampleLeads = [
        {
          client_name: 'Sarah Johnson',
          client_phone: '+1 (555) 234-5678',
          verification_status: 'verified',
          gemini_analysis: {
            room_type: 'kitchen',
            room_state: 'Dated 1990s kitchen with oak cabinets',
            materials: ['oak cabinets', 'laminate countertops', 'vinyl flooring'],
            notes: 'Good bones but needs full cosmetic update.',
          },
        },
        {
          client_name: 'Michael Chen',
          client_phone: '+1 (555) 345-6789',
          verification_status: 'verified',
          gemini_analysis: {
            room_type: 'bathroom',
            room_state: 'Master bathroom with dated tile',
            materials: ['ceramic tile', 'fiberglass tub', 'single sink vanity'],
            notes: 'Water damage near tub may require subfloor repair.',
          },
        },
        {
          client_name: 'Emily Rodriguez',
          client_phone: '+1 (555) 456-7890',
          verification_status: 'pending',
          gemini_analysis: {
            room_type: 'basement',
            room_state: 'Unfinished basement with exposed concrete',
            materials: ['concrete floor', 'exposed joists'],
            notes: 'Good ceiling height. Great potential for finishing.',
          },
        },
        {
          client_name: 'David Thompson',
          client_phone: '+1 (555) 567-8901',
          verification_status: 'verified',
          gemini_analysis: {
            room_type: 'living_room',
            room_state: 'Open concept living room with hardwood floors',
            materials: ['hardwood flooring', 'drywall', 'crown molding'],
            notes: 'Floors need refinishing. Client wants modern minimalist update.',
          },
        },
        {
          client_name: 'Lisa Park',
          client_phone: '+1 (555) 678-9012',
          verification_status: 'verified',
          gemini_analysis: {
            room_type: 'home_office',
            room_state: 'Spare bedroom being converted to home office',
            materials: ['carpet', 'single window', 'standard closet'],
            notes: 'Client needs built-in desk and better lighting.',
          },
        },
      ];

      const leadIds: string[] = [];
      for (let i = 0; i < sampleLeads.length; i++) {
        const lead = sampleLeads[i];
        const createdAt = Timestamp.fromDate(
          new Date(Date.now() - (sampleLeads.length - i) * 24 * 60 * 60 * 1000)
        );

        const leadRef = await addDoc(collection(db, `tenants/${TEST_TENANT_ID}/leads`), {
          ...lead,
          created_at: createdAt,
        });
        leadIds.push(leadRef.id);
      }

      // 4. Create Sample Generations for first lead
      const generationData = {
        original_image_path: 'test/originals/kitchen-before.jpg',
        generated_image_path: 'test/generated/kitchen-modern-1.jpg',
        watermarked_image_path: 'test/watermarked/kitchen-modern-1.jpg',
        prompt_parameters: {
          style: 'Modern Minimalist',
          floor: 'Luxury Vinyl Plank - Gray Oak',
          walls: 'Sherwin Williams - Pure White',
          cabinets: 'Flat panel white with brass hardware',
          countertops: 'White Quartz',
        },
        quote_range_min: 28000,
        quote_range_max: 42000,
        created_by: 'client',
        created_at: Timestamp.now(),
      };

      await addDoc(
        collection(db, `tenants/${TEST_TENANT_ID}/leads/${leadIds[0]}/generations`),
        generationData
      );

      // 5. Create widgetPublic document
      await setDoc(doc(db, 'widgetPublic', TEST_TENANT_ID), {
        company_name: 'Debug Renovations Inc.',
        branding: {
          logo_url: 'https://placehold.co/200x80/1e40af/white?text=Debug+Reno',
          primary_color: '#1e40af',
        },
        widget: {
          enabled: true,
          cta_book_call_url: 'https://calendly.com/test',
          business_phone: '+1 (555) 123-4567',
        },
        pricing: getDefaultPricingConfig(),
      });

      setSeedResult({
        success: true,
        message: `Created tenant, 5 leads, and 1 generation. Tenant ID: ${TEST_TENANT_ID}`,
      });

      // Optionally switch to test tenant
      if (onTenantChange) {
        onTenantChange(TEST_TENANT_ID);
      }
    } catch (error) {
      console.error('Seed error:', error);
      setSeedResult({
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setIsSeeding(false);
    }
  };

  const switchToTestTenant = () => {
    if (onTenantChange) {
      onTenantChange(TEST_TENANT_ID);
    }
    // Also store in localStorage for persistence
    localStorage.setItem('dev_tenant_override', TEST_TENANT_ID);
    window.location.reload();
  };

  const clearTestOverride = () => {
    localStorage.removeItem('dev_tenant_override');
    window.location.reload();
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="bg-amber-100 border-amber-300 text-amber-800 hover:bg-amber-200"
          >
            <Bug className="h-4 w-4 mr-2" />
            Dev Tools
            <ChevronDown
              className={`h-4 w-4 ml-2 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </Button>
        </CollapsibleTrigger>

        <CollapsibleContent className="mt-2">
          <Card className="w-80 shadow-lg border-amber-300">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Database className="h-4 w-4" />
                Development Tools
              </CardTitle>
              <CardDescription className="text-xs">
                Test data seeding and debugging utilities
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Current State */}
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Current Tenant</p>
                <Badge variant="outline" className="font-mono text-xs">
                  {currentTenantId || 'None'}
                </Badge>
              </div>

              {/* Seed Data */}
              <div className="space-y-2">
                <Button
                  onClick={seedTestData}
                  disabled={isSeeding}
                  size="sm"
                  className="w-full"
                  variant="secondary"
                >
                  {isSeeding ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Seeding...
                    </>
                  ) : (
                    <>
                      <Database className="h-4 w-4 mr-2" />
                      Seed Test Data
                    </>
                  )}
                </Button>

                {seedResult && (
                  <p
                    className={`text-xs ${seedResult.success ? 'text-green-600' : 'text-red-600'}`}
                  >
                    {seedResult.message}
                  </p>
                )}
              </div>

              {/* Tenant Switching */}
              <div className="space-y-2 pt-2 border-t">
                <p className="text-xs font-medium text-muted-foreground">Tenant Override</p>
                <div className="flex gap-2">
                  <Button onClick={switchToTestTenant} size="sm" variant="outline" className="flex-1">
                    Use Test Tenant
                  </Button>
                  <Button onClick={clearTestOverride} size="sm" variant="ghost">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Test tenant: <code className="text-xs">{TEST_TENANT_ID}</code>
                </p>
              </div>
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
